import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { Margin, usePDF } from "react-to-pdf";
import WebIntakeReport from "../../Reports/ReportItems/WebIntakeReport";
import SurveyJsPanel from "../../Reports/SurveyJsPanel";
import ThriveModal from "../../../common/ThriveModal";
import ThriveTitle from "../../../common/ThriveTitle";
import { axiosInstance } from "../../../../utils/networking";
import useReportFilter from "../useReportFilter";
import {
  handleB2BClientSurvey,
  handleB2BProviderSurvey,
  handleConferenceSurvey,
  handleEventSurvey,
  handleReferralSurvey,
} from "../legacyExports";

const PRIMARY = "#9D2362";

const SUB_TABS = [
  { value: "webintake", label: "Web Intake" },
  { value: "surveys", label: "Surveys" },
];

type SurveyReport = {
  id: string;
  title: string;
  download?: () => void;
};

const SURVEY_REPORTS: SurveyReport[] = [
  { id: "6488788edd1f6f5e5c5e5d7a", title: "Discovery Question Report" },
  { id: "6504ae6817baa329c061dee7", title: "Business Needs Assessment Report" },
  {
    id: "64f75c4287cfd9719d4904e1",
    title: "Resource Referral Report",
    download: handleReferralSurvey,
  },
  {
    id: "65412693f9ecc07bf529219f",
    title: "Event Survey Report",
    download: handleEventSurvey,
  },
  {
    id: "654b20727276d3350fde26ba",
    title: "Conference Survey Report",
    download: handleConferenceSurvey,
  },
  {
    id: "65585ca5da7bea193668034e",
    title: "B2B Client Survey Report",
    download: handleB2BClientSurvey,
  },
  {
    id: "655869c8da7bea1936680388",
    title: "B2B Vendor Survey Report",
    download: handleB2BProviderSurvey,
  },
];

// Prefix each question title with its page title so the analytics panel reads
// unambiguously (lifted from the legacy Reports page).
const refineSurveyContent = (surveyContent: any) => {
  if (surveyContent?.pages) {
    for (const pageContent of surveyContent.pages) {
      const pageTitle = pageContent.title;
      for (const elementContent of pageContent.elements) {
        elementContent.title = pageTitle + " - " + elementContent.title;
      }
    }
  }
  return surveyContent;
};

interface IntakeTabProps {
  sub: string;
  setSub: (sub: string) => void;
}

/** Web intake report plus the SurveyJS analytics panels. */
const IntakeTab: React.FC<IntakeTabProps> = ({ sub, setSub }) => {
  const { toPDF, targetRef } = usePDF({
    filename: "Report.pdf",
    page: { margin: Margin.SMALL, format: "letter" },
    overrides: { canvas: { removeContainer: false } },
  });
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [surveyTitle, setSurveyTitle] = useState("");
  const [surveyContent, setSurveyContent] = useState<any>({});
  const [surveyResult, setSurveyResult] = useState<any[]>([]);
  // The layout mounts this tab with only { sub, setSub }; read the shared,
  // URL-backed range so the embedded Web Intake report follows the hub filter
  // instead of its own pickers. (The survey panels are not date-filtered.)
  const { start, end, consultant } = useReportFilter();

  const active = SUB_TABS.some((t) => t.value === sub) ? sub : "webintake";

  const openSurvey = async (report: SurveyReport) => {
    setSurveyTitle(report.title);
    setSurveyContent(null);
    setSurveyResult([]);
    setSurveyOpen(true);
    const survey = await axiosInstance.get(`/survey?id=${report.id}`);
    setSurveyContent(refineSurveyContent(survey.data.survey.survey_content));
    const results = await axiosInstance.get(
      `/survey/results?survey_id=${report.id}`,
    );
    setSurveyResult(
      (results.data.results || []).map((result: any) => result.survey_result),
    );
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
        {SUB_TABS.map((tab) => (
          <Chip
            key={tab.value}
            label={tab.label}
            onClick={() => setSub(tab.value)}
            sx={{
              fontWeight: 600,
              backgroundColor: active === tab.value ? PRIMARY : "#f0f0f0",
              color: active === tab.value ? "white" : "#5a5a63",
              "&:hover": {
                backgroundColor: active === tab.value ? "#7A1B4D" : "#e3e3e3",
              },
            }}
          />
        ))}
      </Stack>

      {active === "webintake" && (
        <>
          {consultant && (
            <Typography variant="caption" sx={{ color: "#8a8a93", display: "block", mb: 1 }}>
              Web intake covers every consultant — the consultant filter is not
              applied here, since most intakes are unassigned when they arrive.
            </Typography>
          )}
          <WebIntakeReport controlledRange={{ start, end }} />
        </>
      )}

      {active === "surveys" && (
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#2c2c34", mb: 0.5 }}
          >
            Survey analytics
          </Typography>
          <Typography variant="body2" sx={{ color: "#8a8a93", mb: 2 }}>
            Aggregated SurveyJS results. "Download Raw" exports every response
            as CSV.
          </Typography>
          <Table sx={{ maxWidth: 900 }}>
            <TableBody>
              {SURVEY_REPORTS.map((report) => (
                <TableRow key={report.id}>
                  <TableCell>
                    <Button
                      onClick={() => openSurvey(report)}
                      sx={{ color: PRIMARY, fontWeight: 600 }}
                    >
                      {report.title}
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    {report.download ? (
                      <Button onClick={report.download}>Download Raw</Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <ThriveModal
        open={surveyOpen}
        width={"1000px"}
        height={"600px"}
        contentComponent={
          <>
            <ThriveTitle title={surveyTitle} setOpen={setSurveyOpen} />
            <Box sx={{ position: "absolute", marginTop: "-42px" }}>
              <Button
                sx={{ marginLeft: "10px" }}
                color={"warning"}
                variant="contained"
                onClick={() => {
                  toPDF();
                  axiosInstance.post("/export_log", {
                    export_type: "report_pdf",
                    record_count: surveyResult.length,
                    filters: { report: surveyTitle },
                  });
                }}
              >
                Print
              </Button>
            </Box>
            <Box
              sx={{
                margin: 1,
                height: "530px",
                overflow: "auto",
                backgroundColor: "white",
              }}
            >
              {surveyOpen && surveyContent ? (
                <SurveyJsPanel
                  surveyTitle={surveyTitle}
                  pdfref={targetRef}
                  surveyContent={surveyContent}
                  surveyResult={surveyResult}
                />
              ) : (
                <Typography sx={{ p: 3, color: "#8a8a93" }}>
                  Loading survey responses…
                </Typography>
              )}
            </Box>
          </>
        }
      />
    </Box>
  );
};

export default IntakeTab;
